import type { MicGraph } from './browserSource';

/**
 * pcmRecorder — 진단 녹음의 **원음 보관**. f0 프레임과 같은 마이크 그래프에 붙어
 * 모노 Float32 PCM을 모으고, 마지막 N초만 남긴다.
 *
 * **GOAL.md의 "raw PCM은 JS로 넘어오지 않는다" 불변식과 충돌하지 않는다.** 그 불변식은
 * 네이티브 경로(네이티브가 오디오·엔진·타임베이스를 소유하고 JS로는 Analysis Frame만
 * 건너오는 경로)를 규율한다. 여기는 네이티브 엔진이 아예 없는 **브라우저 경로**이고,
 * 이 경로의 f0 추정은 애초에 JS에서 돈다(`micFrames.ts` — "이건 엔진이 아니다").
 * 네이티브 앱에서 원음을 받으려면 파일 경로를 돌려주는 브리지 메서드가 따로 있어야 하고,
 * 그건 여기가 아니다.
 *
 * `ScriptProcessorNode`를 쓴다. 폐기 예정 API인 건 맞지만 대안인 AudioWorklet은 별도
 * 모듈 파일을 번들에서 URL로 로드해야 해서 Vite 설정이 붙는다. 여기서 하는 일은 콜백에서
 * 입력 버퍼를 복사하는 것뿐이라 워크릿의 이점(오디오 스레드 실행)이 사실상 없다.
 *
 * 오디오 IO 모듈이다 — 순수 부분(`toTakeJson`·`encodeWav`)은 `takeExport.ts`에 있다.
 */

/**
 * 열어 달라고 요청할 샘플레이트.
 *
 * **받는다는 보장은 없다** — 기기 기본값이 48000인 경우가 흔하고, 브라우저가 요청을
 * 거부하면 기본 레이트로 열린다. 그래서 내보내는 데이터에는 언제나 `ctx.sampleRate`에서
 * 다시 읽은 **실제 레이트**를 싣는다(`PcmTake.sampleRate`).
 */
export const PCM_TARGET_SAMPLE_RATE = 44_100;

/** 한 번에 넘어오는 표본 수. 2의 거듭제곱만 받는다. */
const BUFFER_SIZE = 4096;

export interface PcmTake {
  /** 모노 Float32 표본(-1..1). 보관 창을 넘긴 앞부분은 버려져 있다. */
  samples: Float32Array;
  /** 실제로 열린 레이트. 요청값(`PCM_TARGET_SAMPLE_RATE`)과 다를 수 있다. */
  sampleRate: number;
  /** `samples.length / sampleRate`. */
  durationSec: number;
  /** 보관 창을 넘겨 **앞부분이 잘렸는지**. 잘렸으면 이 테이크는 끝의 N초다. */
  trimmed: boolean;
}

export interface PcmRecorder {
  /** 마이크 그래프에 붙는다. 이미 붙어 있으면 먼저 떼고 다시 붙는다. */
  attach: (graph: MicGraph) => void;
  /** 그래프에서 뗀다. 모은 표본은 그대로 남는다(`take()`로 가져갈 수 있다). */
  detach: () => void;
  /** 지금까지 모은 것. 마이크가 한 번도 안 열렸으면 null. */
  take: () => PcmTake | null;
  /** 모은 것을 버린다(다음 테이크 시작). */
  reset: () => void;
}

/**
 * @param keepSec 보관할 마지막 구간(초). 이보다 오래 녹음하면 **앞부터** 버린다 —
 *   분석 창이 마지막 N초라서, 넘친 쪽을 버려야 남는 것이 분석 대상과 같아진다.
 */
export const createPcmRecorder = (keepSec: number): PcmRecorder => {
  let ctx: AudioContext | null = null;
  let processor: ScriptProcessorNode | null = null;
  let sink: GainNode | null = null;
  let chunks: Float32Array[] = [];
  let total = 0;
  let trimmed = false;
  let sampleRate = 0;

  /** 보관 창을 넘긴 앞쪽 청크를 버린다. */
  const trim = (): void => {
    const max = Math.floor(keepSec * sampleRate);
    if (max <= 0) {
      return;
    }
    while (total > max && chunks.length > 1) {
      total -= chunks[0].length;
      chunks.shift();
      trimmed = true;
    }
  };

  const detach = (): void => {
    processor?.disconnect();
    sink?.disconnect();
    if (processor) {
      processor.onaudioprocess = null;
    }
    processor = null;
    sink = null;
  };

  return {
    attach: (graph: MicGraph): void => {
      detach();
      ctx = graph.ctx;
      sampleRate = graph.ctx.sampleRate;
      const node = graph.ctx.createScriptProcessor(BUFFER_SIZE, 1, 1);
      node.onaudioprocess = (event) => {
        // `getChannelData`는 재사용되는 버퍼를 돌려준다 — 복사하지 않으면 다음 콜백이
        // 이미 보관한 표본을 덮어쓴다.
        chunks.push(new Float32Array(event.inputBuffer.getChannelData(0)));
        total += event.inputBuffer.length;
        trim();
      };
      // Chrome은 destination까지 이어지지 않은 ScriptProcessor를 돌리지 않는다.
      // 게인 0인 노드를 사이에 둬서 마이크가 스피커로 되돌아 나가지 않게 한다.
      const zero = graph.ctx.createGain();
      zero.gain.value = 0;
      graph.source.connect(node);
      node.connect(zero);
      zero.connect(graph.ctx.destination);
      processor = node;
      sink = zero;
    },
    detach,
    take: (): PcmTake | null => {
      if (!ctx || total === 0) {
        return null;
      }
      const samples = new Float32Array(total);
      let at = 0;
      for (const chunk of chunks) {
        samples.set(chunk, at);
        at += chunk.length;
      }
      return {
        samples,
        sampleRate,
        durationSec: sampleRate > 0 ? samples.length / sampleRate : 0,
        trimmed,
      };
    },
    reset: (): void => {
      chunks = [];
      total = 0;
      trimmed = false;
    },
  };
};
